function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}


function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest('.shopify-section').id.replace('shopify-section-', '');
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }

      // Workaround for safari iframe bug
      const formerStyle = deferredElement.getAttribute('style');
      deferredElement.setAttribute('style', 'display: block;');
      window.setTimeout(() => {
        deferredElement.setAttribute('style', formerStyle);
      }, 0);
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      this.dispatchProductSelectEvent();

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  getAllSelectedOptions() {
    const options = [];
    this.querySelectorAll('fieldset, .product-form__input--dropdown').forEach((group) => {
      const checked = group.querySelector('input:checked') || group.querySelector('select option[selected]');
      if (checked) {
        options.push({ name: checked.dataset.optionName || '', value: checked.value });
      }
    });
    return options;
  }

  dispatchProductSelectEvent() {
    const { ProductSelectEvent } = window.StandardEvents || {};
    if (!ProductSelectEvent) return;

    const deferred = ProductSelectEvent.createPromise();
    this.pendingSelectPromise = deferred;

    this.dispatchEvent(
      new ProductSelectEvent({
        product: {
          id: this.dataset.productId,
          title: this.dataset.productTitle,
          handle: this.dataset.productHandle,
        },
        selectedOptions: this.getAllSelectedOptions(),
        promise: deferred.promise,
      })
    );
  }

  takePendingSelectPromise() {
    const deferred = this.pendingSelectPromise;
    this.pendingSelectPromise = null;
    return deferred;
  }

  resolvePendingSelectPromise(variant, sourceVariantSelects = this) {
    const deferred = this.takePendingSelectPromise();
    if (!deferred) return;

    if (variant) {
      deferred.resolve({
        variant: {
          id: variant.id,
          title: variant.title,
          availableForSale: variant.available,
          price: {
            amount: sourceVariantSelects?.dataset.selectedPriceAmount,
            currencyCode: sourceVariantSelects?.dataset.currencyCode,
          },
          selectedOptions: this.getAllSelectedOptions(),
        },
      });
    } else {
      deferred.resolve({ variant: null });
    }
  }

  rejectPendingSelectPromise(error) {
    this.takePendingSelectPromise()?.reject(error);
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class BulkAdd extends HTMLElement {
  static ASYNC_REQUEST_DELAY = 250;

  constructor() {
    super();
    this.queue = [];
    this.setRequestStarted(false);
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });

    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, BulkAdd.ASYNC_REQUEST_DELAY);
  }

  sendRequest(queue) {
    this.setRequestStarted(true);
    const items = {};

    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));

    this.updateMultipleQty(items);
  }

  setRequestStarted(requestStarted) {
    this._requestStarted = requestStarted;
  }

  get requestStarted() {
    return this._requestStarted;
  }

  getCartQuantityForLine(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    return parseInt(input?.dataset.cartQuantity || input?.getAttribute('value') || '0', 10) || 0;
  }

  startCartLinesUpdate(items) {
    const { CartLinesUpdateEvent } = window.StandardEvents || {};
    if (!CartLinesUpdateEvent) return;

    const linesByAction = Object.entries(items).reduce((groups, [variantId, quantity]) => {
      const nextQuantity = parseInt(quantity, 10);
      const currentQuantity = this.getCartQuantityForLine(variantId);

      if (Number.isNaN(nextQuantity) || currentQuantity === nextQuantity) return groups;

      const action = currentQuantity === 0 ? 'add' : nextQuantity === 0 ? 'remove' : 'update';
      let line;
      if (action === 'add') {
        line = { merchandiseId: variantId, quantity: nextQuantity };
      } else {
        const lineKey = this.querySelector(`[data-quantity-variant-id="${variantId}"]`)?.dataset.quantityLineKey;
        // No AJAX line key on the row — likely cached HTML rendered before this
        // attribute landed. Skip rather than emit an event with id: ''.
        if (!lineKey) return groups;
        line = { id: lineKey, quantity: nextQuantity };
      }

      if (!groups[action]) groups[action] = [];
      groups[action].push(line);
      return groups;
    }, {});

    const deferreds = Object.entries(linesByAction).map(([action, lines]) => {
      const deferred = CartLinesUpdateEvent.createPromise();
      this.dispatchEvent(
        new CartLinesUpdateEvent({
          action,
          context: 'product',
          lines,
          promise: deferred.promise,
        })
      );
      return deferred;
    });

    return {
      resolve: (parsedState) => {
        const payload = { cart: CartLinesUpdateEvent.createCartFromAjaxResponse(parsedState) };
        deferreds.forEach((deferred) => deferred.resolve(payload));
      },
      reject: (error) => {
        deferreds.forEach((deferred) => deferred.reject(error));
      },
    };
  }

  dispatchCartErrorEvent(message, code) {
    const { CartErrorEvent } = window.StandardEvents || {};
    if (!CartErrorEvent) return;

    this.dispatchEvent(new CartErrorEvent({ error: message, code }));
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      event.target.setAttribute('value', inputValue);
      this.startQueue(index, inputValue);
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}

class CartPerformance {
  static #metric_prefix = "cart-performance"

  static createStartingMarker(benchmarkName) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    return performance.mark(`${metricName}:start`);
  }

  static measureFromEvent(benchmarkName, event) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`, {
      startTime: event.timeStamp
    });

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }

  static measureFromMarker(benchmarkName, startMarker) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      startMarker.name,
      `${metricName}:end`
    );
  }

  static measure(benchmarkName, callback) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`);

    callback();

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }
}
;(function(){var l=document.createElement("link");l.rel="stylesheet";l.href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;1,300&display=swap";document.head.appendChild(l);window.addEventListener("load",function(){setTimeout(function(){var i=document.querySelector(".opo-i");if(i&&!i.querySelector(".opo-moon")){var c='<circle cx="13" cy="13" r="10" fill="none" stroke="#C9A961" stroke-width="1.2"/>';var s=function(x){return '<svg width="26" height="26" viewBox="0 0 26 26" aria-hidden="true">'+c+x+'</svg>'};var d=document.createElement("div");d.className="opo-moon";d.style.cssText="display:flex;justify-content:center;gap:14px;margin-bottom:22px";d.innerHTML=s("")+s('<path d="M13 3a10 10 0 0 1 0 20a6 10 0 0 0 0-20z" fill="#C9A961"/>')+s('<circle cx="13" cy="13" r="10" fill="#C9A961"/>')+s('<path d="M13 3a10 10 0 0 0 0 20a6 10 0 0 1 0-20z" fill="#C9A961"/>')+s("");i.insertBefore(d,i.firstChild)}document.querySelectorAll("footer *").forEach(function(e){if(e.children.length)return;var t=(e.textContent||"").trim().toLowerCase();if(t.indexOf("s'inscrire")===0)e.style.setProperty("color","#FBF6EA","important");else if(t.indexOf("recevez")===0)e.style.setProperty("color","#E8DFC8","important")})},2800)})})();;(function(){if(location.pathname!=="/")return;var css=`.opo-moon{display:none!important}.opo-h{background:linear-gradient(180deg,#1F2790,#2B33A5);color:#F3E9D2;padding:72px 64px 96px;display:flex;flex-wrap:wrap;gap:56px;align-items:center;justify-content:center}.opo-h__t{flex:1 1 420px;max-width:560px}.opo-h__e{font-size:12px;letter-spacing:.3em;text-transform:uppercase;color:#C9A961}.opo-h h1{margin:22px 0 0;font-family:"Cormorant Garamond",serif;font-weight:300;font-size:68px;line-height:1.04;color:#FBF6EA}.opo-h i{display:block;width:64px;height:1px;background:#C9A961;margin:30px 0}.opo-h__t p{margin:0;font-size:18px;line-height:1.7;color:#E8DFC8}.opo-h__b{display:flex;gap:16px;margin-top:38px;flex-wrap:wrap}.opo-h__b a{padding:16px 34px;border-radius:999px;font-size:12px;letter-spacing:.2em;text-transform:uppercase;text-decoration:none}.opo-h__b .a1{background:#C9A961;color:#0F1245}.opo-h__b .a2{border:1px solid #C9A961;color:#F3E9D2}.opo-h__p{position:relative;flex:0 1 340px;width:340px;height:480px}.opo-h__p .fr{position:absolute;inset:-14px;border:1px solid #C9A961;border-radius:170px 170px 0 0}.opo-h__p img{position:absolute;inset:0;width:100%;height:100%;object-fit:cover;border-radius:170px 170px 0 0}.opo-c{background:#FBF6EA;padding:96px 64px;text-align:center}.opo-c h2{font-family:"Cormorant Garamond",serif;font-weight:300;font-size:46px;color:#20268C;margin:0 auto;max-width:760px}.opo-c>p{margin:22px auto 0;max-width:640px;font-size:17px;line-height:1.8;color:#5B4F78}.opo-c__g{display:flex;flex-wrap:wrap;gap:28px;justify-content:center;margin-top:64px}.opo-c__g a{flex:1 1 300px;max-width:380px;text-decoration:none}.opo-c__a{height:380px;border-radius:190px 190px 12px 12px;border:1px solid #C9A961;outline:1px solid #C9A961;outline-offset:8px;background-size:cover;background-position:center}.opo-c h3{margin:34px 0 8px;font-family:"Cormorant Garamond",serif;font-weight:400;font-size:30px;color:#20268C}.opo-c__g p{margin:0;font-size:15px;line-height:1.7;color:#5B4F78}@media(max-width:800px){.opo-h{padding:48px 24px}.opo-h h1{font-size:44px}.opo-h__p{width:260px;height:370px;flex:0 0 260px}.opo-c{padding:64px 24px}}`;var st=document.createElement("style");st.textContent=css;document.head.appendChild(st);var H=`<section class="opo-h"><div class="opo-h__t"><div class="opo-h__e">Traiteur post-partum, Paris</div><h1>Prendre soin<br>après la naissance</h1><i></i><p>Des repas pensés comme un rituel : chauds, nourrissants, préparés avec soin pour la mère, pendant que sa famille l'entoure.</p><div class="opo-h__b"><a class="a1" href="/collections/box-repas-post-partum">Commander la box</a><a class="a2" href="#opo-r">Découvrir le rituel</a></div></div><div class="opo-h__p"><div class="fr"></div><img src="https://cdn.shopify.com/s/files/1/1083/0158/2666/files/photo_14_BD_5a4ece0d-0c81-4289-b6d0-af695c67fc13.jpg?v=1790500415" alt="OPONOPONO"></div></section><section class="opo-c"><h2>Un temps sacré pour la mère</h2><p>Le post-partum est une période de convalescence. Partout dans le monde, on entoure la jeune mère et on cuisine pour elle. OPONOPONO remet ce geste au goût du jour.</p><div class="opo-c__g"><a href="/collections/box-repas-post-partum"><div class="opo-c__a" style="background-image:url(https://cdn.shopify.com/s/files/1/1083/0158/2666/collections/image_d17efe2d-fc67-4d27-9ff1-2a70a84e03f0.png?v=1791481745)"></div><h3>La box post-partum</h3><p>Des repas livrés chaque mardi, pour récupérer en douceur.</p></a><a href="/pages/mama-blessing"><div class="opo-c__a" style="background-image:url(https://cdn.shopify.com/s/files/1/1083/0158/2666/collections/fondant-choco-collagene_9eb462b6-926d-4d6d-b47a-a8731afa5d26.jpg?v=1791481699)"></div><h3>Mama Blessing</h3><p>Un buffet pour célébrer la mère avant la naissance.</p></a><a href="/pages/surgeles"><div class="opo-c__a" style="background:linear-gradient(160deg,#4B52B8,#14185F)"></div><h3>La gamme surgelée</h3><p>Des plats faits maison, prêts quand la fatigue arrive.</p></a></div></section>`;function go(){var b=document.querySelector("main .banner");if(!b||document.querySelector(".opo-h"))return;var sec=b.closest(".shopify-section");var h=document.createElement("div");h.innerHTML=H;sec.parentNode.insertBefore(h,sec);sec.style.display="none";var i=document.querySelector(".opo-i");if(i)i.id="opo-r"}if(document.readyState!=="loading")go();else document.addEventListener("DOMContentLoaded",go)})();;(function(){function f(){document.querySelectorAll(".product__tax,.tax-note").forEach(function(e){e.textContent="Taxes incluses. Livraison incluse."})}window.addEventListener("load",function(){f();setTimeout(f,1500)})})();;(function(){var s=document.createElement("style");s.textContent="html:root:root:root body .opo-i p{font-size:17px!important;line-height:1.75!important}html:root:root:root body .opo-i h3{font-size:28px!important}";document.head.appendChild(s);function f(){var a=document.querySelectorAll(".opo-c__g a");if(a.length<3)return;var d=[["/collections/box-repas-post-partum",null,"Les repas post-partum","Pour les jeunes mamans, livrés à domicile ou à la maternité, en abonnement ou à l'unité."],["/products/le-pack-naissance","https://cdn.shopify.com/s/files/1/1083/0158/2666/files/pack-naissance.png?v=1790341202","Pack naissance et carte cadeau","Le plus beau cadeau de naissance."],["/pages/evenements-et-buffets","https://cdn.shopify.com/s/files/1/1083/0158/2666/files/catering-pro.jpg?v=1791540136","Mama Blessing et buffets","Célébrer la maman : anniversaire, baby shower et tous vos moments à partager."]];d.forEach(function(x,i){a[i].setAttribute("href",x[0]);if(x[1])a[i].querySelector(".opo-c__a").setAttribute("style","background-image:url("+x[1]+")");a[i].querySelector("h3").textContent=x[2];a[i].querySelector("p").textContent=x[3]})}document.addEventListener("DOMContentLoaded",f);window.addEventListener("load",function(){f();setTimeout(f,800)})})();;(function(){var s=document.createElement("style");s.textContent="html:root:root:root body .opo-c>h2,html:root:root:root body .opo-i h2,html:root:root:root body .opo-p h2{font-size:40px!important;line-height:1.15!important}html:root:root:root body .opo-c>p{max-width:none!important;white-space:nowrap!important;text-align:center!important;font-size:clamp(12px,1.5vw,19px)!important}html:root:root:root body .opo-i{background:#F3EBDD!important;padding:88px 24px!important}html:root:root:root body .opo-i>p{max-width:640px!important;margin:0 auto 14px!important;font-size:19px!important;line-height:1.7!important}html:root:root:root body .opo-why{max-width:1040px;margin:64px auto 0}html:root:root:root body .opo-why h3{font-size:30px!important;margin:0 0 36px!important}html:root:root:root body .opo-cards{display:grid;grid-template-columns:repeat(2,1fr);gap:20px;text-align:left}html:root:root:root body .opo-cards>div{background:#FBF6EA;border:1px solid #B9A087;border-radius:18px;padding:28px}html:root:root:root body .opo-cards b{display:block;font-family:Cormorant Garamond,serif;font-size:25px;font-weight:600;line-height:1.2;margin:0 0 8px;color:#3B2A22}html:root:root:root body .opo-cards i{display:block;font-style:normal;color:#C9A961;letter-spacing:.2em;font-size:13px;margin-bottom:10px}html:root:root:root body .opo-cards p{margin:0!important;max-width:none!important;font-size:17px!important;line-height:1.65!important;text-align:left!important}html:root:root:root body .opo-end{max-width:680px!important;margin:40px auto 0!important;font-size:19px!important;line-height:1.7!important;text-align:center!important}html:root:root:root body .opo-f{background:#E8DCCB!important}html:root:root:root body .opo-f h3{font-size:24px!important}html:root:root:root body .opo-f p{font-size:17px!important;line-height:1.65!important}html:root:root:root body .opo-p h3{font-size:26px!important}html:root:root:root body .opo-p b{font-size:20px!important}html:root:root:root body .opo-p em{font-size:16px!important}@media(max-width:760px){html:root:root:root body .opo-cards{grid-template-columns:1fr}html:root:root:root body .opo-c>p{white-space:normal!important;font-size:16px!important}html:root:root:root body .opo-c>h2,html:root:root:root body .opo-i h2,html:root:root:root body .opo-p h2{font-size:32px!important}}";document.head.appendChild(s);function g(){var w=document.querySelector(".opo-why");if(!w||w.getAttribute("data-n"))return;w.setAttribute("data-n","1");w.innerHTML='<h3>Pourquoi bien se nourrir après la naissance ?</h3><div class="opo-cards"><div><i>01</i><b>Un corps qui se reconstruit</b><p>Le post-partum ne dure pas quelques semaines : le corps met au moins trois ans à se remettre d’une grossesse et d’une naissance.</p></div><div><i>02</i><b>Une transformation intérieure</b><p>La matrescence bouleverse l’identité même de la femme, dans la tête et dans le cœur. Être bien nourrie et entourée est un beau geste de soin, bien après les premières semaines.</p></div><div><i>03</i><b>Des besoins élevés</b><p>Protéines, fer, bonnes graisses, zinc, vitamines et minéraux : une alimentation riche, chaude et facile à digérer soutient la cicatrisation, l’énergie, l’humeur et le sommeil.</p></div><div><i>04</i><b>Une sagesse ancestrale</b><p>Mois d’or chinois, ayurveda, cuarentena d’Amérique latine : partout, on cuisine pour la jeune mère pendant les quarante jours qui suivent la naissance.</p></div></div><p class="opo-end">Chez OPONOPONO, nous remettons cette sagesse au goût du jour : des repas chauds, nourrissants et équilibrés, pour vous soutenir, vous et votre bébé, sans que vous ayez à y penser.</p>'}document.addEventListener("DOMContentLoaded",g);window.addEventListener("load",function(){g();setTimeout(g,300);setTimeout(g,900)})})();(function(){function r(){var p=document.querySelector(".announcement-bar__message");if(!p||p.getAttribute("data-r"))return;p.setAttribute("data-r","1");var ic=p.querySelector(".opo-truck svg"),t=p.querySelector("span:not(.opo-truck)");if(!ic||!t)return;var o=ic.innerHTML,M=[[o,t.textContent],['<path d="M12 3l2.2 5.8L20 11l-5.8 2.2L12 19l-2.2-5.8L4 11l5.8-2.2z"/>',"Mama Blessing et buffets sur mesure, sur réservation à Paris"],['<path d="M12 2v20M4 7l16 10M20 7L4 17"/>',"Gamme surgelée : arrivée très bientôt, sortie prévue en novembre"]],i=0;p.style.transition="opacity .5s";setInterval(function(){p.style.opacity=0;setTimeout(function(){i=(i+1)%2;ic.innerHTML=M[i][0];t.textContent=M[i][1];p.style.opacity=1},500)},4500)}document.addEventListener("DOMContentLoaded",r);window.addEventListener("load",r)})();;(function(){var s=document.createElement("style");s.textContent="html:root:root:root body .opo-dur,html:root:root:root body .opo-why .opo-dur{display:none!important}";document.head.appendChild(s)})();;(function(){var s=document.createElement("style");s.textContent="html:root:root:root body .opo-c>p{font-size:17px!important}@media(max-width:1099px){html:root:root:root body .opo-c>p{white-space:normal!important;font-size:16px!important;max-width:640px!important;margin-left:auto!important;margin-right:auto!important}}";document.head.appendChild(s)})();;(function(){var st=document.createElement("style");st.textContent="html:root:root:root:root body .rte div[style*=grid] h3{color:#B08D3C!important;text-transform:uppercase!important;letter-spacing:.22em!important;font-size:18px!important;font-weight:600!important}#opo-zone{border:1px solid #B9A087;border-radius:18px;padding:20px;margin:0 0 20px;background:#FBF6EA}#opo-zone .opo-zt{margin:0 0 12px;font-size:13px;letter-spacing:.2em;text-transform:uppercase;color:#B08D3C}#opo-zone label{display:flex;align-items:center;gap:10px;padding:10px 12px;border:1px solid #B9A087;border-radius:12px;margin:0 0 8px;cursor:pointer}#opo-zone label.on{border:2px solid #C9A961}#opo-zone em{margin-left:auto;font-style:normal}.opo-fee cart-remove-button,.opo-fee .quantity,.opo-fee quantity-input{display:none!important}";document.head.appendChild(st);function tr(){document.querySelectorAll("h2,h3").forEach(function(h){if(h.textContent.trim()==="You may also like")h.textContent="Vous aimerez aussi"})}function hz(){document.querySelectorAll("fieldset.product-form__input").forEach(function(f){var l=f.querySelector("legend");if(l&&/zone/i.test(l.textContent))f.style.display="none"});document.querySelectorAll(".product__tax,.tax-note").forEach(function(e){e.textContent="Taxes incluses. Livraison calculée dans le panier selon votre zone."})}function run(){tr();hz()}document.addEventListener("DOMContentLoaded",run);window.addEventListener("load",function(){run();setTimeout(run,800);setTimeout(run,2600)});if(location.pathname.replace(/\/$/,"")!=="/cart")return;var Z=[["Paris",0],["Petite couronne (92-93-94)",15],["Grande couronne (78)",25]],busy=false,tm;function J(u,o){return fetch(u,o).then(function(r){return r.json()})}function P(u,b){return J(u,{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify(b)})}function ui(zi,n){var b=document.getElementById("opo-zone");if(!n){if(b)b.remove();return}document.querySelectorAll(".cart-item").forEach(function(r){if(r.querySelector("a[href*=frais-de-livraison]"))r.classList.add("opo-fee")});var k=zi+","+n;if(!b){b=document.createElement("div");b.id="opo-zone";var t=document.querySelector(".cart__footer");if(!t)return;t.insertBefore(b,t.firstChild)}if(b.getAttribute("data-k")!==k){b.setAttribute("data-k",k);b.innerHTML='<p class="opo-zt">Zone de livraison</p>'+Z.map(function(x,i){return '<label class="'+(i===zi?"on":"")+'"><input type="radio" name="opozone" value="'+i+'"'+(i===zi?" checked":"")+"><span>"+x[0]+"</span><em>"+(x[1]?"+ "+x[1]+" €":"incluse")+"</em></label>"}).join("");b.querySelectorAll("input").forEach(function(r){r.onchange=function(){var i=+r.value;P("/cart/update.js",{attributes:{"Zone de livraison":Z[i][0]}}).then(function(){busy=false;sync()})}})}var ck=document.getElementById("checkout");if(ck){ck.disabled=zi<0;ck.style.opacity=zi<0?".4":""}}function sync(){if(busy)return;busy=true;Promise.all([J("/cart.js"),J("/products/frais-de-livraison.js")]).then(function(a){var c=a[0],p=a[1],ids=p.variants.map(function(v){return v.id}),fee=c.items.filter(function(i){return ids.indexOf(i.variant_id)>-1}),n=c.items.length-fee.length,z=c.attributes["Zone de livraison"],zi=-1;Z.forEach(function(x,i){if(x[0]===z)zi=i});var want=n>0&&zi>0?p.variants.filter(function(v){return v.title===Z[zi][0]})[0].id:null;var ok=want?fee.length===1&&fee[0].variant_id===want&&fee[0].quantity===window.opoD(c):fee.length===0;if(ok){busy=false;ui(zi,n);return}var u={};fee.forEach(function(f){u[f.key]=0});P("/cart/update.js",{updates:u}).then(function(){return want?P("/cart/add.js",{items:[{id:want,quantity:window.opoD(c)}]}):0}).then(function(){location.reload()})}).catch(function(){busy=false})}function go(){sync();var c=document.querySelector("cart-items")||document.body;new MutationObserver(function(){clearTimeout(tm);tm=setTimeout(sync,500)}).observe(c,{childList:true,subtree:true})}if(document.readyState!=="loading")go();else document.addEventListener("DOMContentLoaded",go)})();;(function(){if(location.pathname.replace(/\/$/,"")!=="/cart")return;setInterval(function(){var z=document.getElementById("opo-zone"),b=document.querySelector(".cart__blocks");if(z&&b&&z.parentNode!==b)b.insertBefore(z,b.firstChild);document.querySelectorAll(".product-option").forEach(function(e){if(/^Zone de livraison/.test(e.textContent.trim()))e.style.display="none"})},400)})();;(function(){setInterval(function(){document.querySelectorAll(".related-products__heading").forEach(function(h){if(/you may also like/i.test(h.textContent))h.textContent="Vous aimerez aussi"});if(location.pathname!="/cart"||document.getElementById("opo-note"))return;var b=document.querySelector(".cart__blocks");if(!b)return;var d=document.createElement("div");d.id="opo-note";d.style.cssText="margin:20px 0;text-align:left";d.innerHTML='<label for="opo-nt" style="display:block;font-weight:600;color:#3B2A22;margin-bottom:8px">Aucune date ne convient ? Un mot pour nous</label><textarea id="opo-nt" name="note" form="cart" rows="3" placeholder="Indiquez le mois souhaité ou votre date de terme, nous reviendrons vers vous." style="width:100%;padding:12px;border:1px solid #B9A087;border-radius:8px;background:#FBF6EA;color:#3B2A22;font:inherit"></textarea>';var k=document.getElementById("checkout");k?k.before(d):b.appendChild(d);var t=d.querySelector("textarea");fetch("/cart.js").then(function(r){return r.json()}).then(function(c){if(c.note&&!t.value)t.value=c.note});t.addEventListener("change",function(){fetch("/cart/update.js",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({note:t.value})})})},700)})();;window.opoD=function(c){var q=0;c.items.forEach(function(i){if(/pack-naissance/.test(i.url))q+=i.quantity});return q?2*q:1};;(function(){function a(){var f=document.querySelector("footer");if(!f||document.getElementById("opo-ct"))return;var d=document.createElement("div");d.id="opo-ct";d.style.cssText="text-align:center;margin:20px 0;font-size:15px";d.innerHTML='Contact : <a href="mailto:contact@oponopono-paris.com" style="text-decoration:underline;color:inherit">contact@oponopono-paris.com</a>';var p=f.querySelector(".footer__content-bottom, .footer__content-top")||f;p.insertBefore(d,p.firstChild)}a();document.addEventListener("DOMContentLoaded",a);setTimeout(a,800)})();;(function(){var st=document.createElement("style");st.id="opo-v3";st.textContent='html body .menu-drawer,html body .menu-drawer__inner-container{background:#FFFCF5!important}html body .menu-drawer a,html body .menu-drawer summary,html body .menu-drawer .menu-drawer__menu-item,html body .menu-drawer .list-menu__item,html body .menu-drawer span,html body .menu-drawer button,html body .menu-drawer .menu-drawer__account,html body .menu-drawer svg{color:#3B2A22!important;fill:currentColor}html body .menu-drawer .menu-drawer__menu-item{background:transparent!important;border-bottom:1px solid rgba(184,134,61,.35)!important}html body header .header__heading-link .h2::before{content:"OPONOPONO"!important;font-family:"Cormorant Garamond",Georgia,serif!important;font-weight:500!important;font-size:2.3rem!important;letter-spacing:.34em!important;margin:0 -.34em 0 0!important;padding:0!important;background:linear-gradient(100deg,#E9D6A0 0,#F8EBC4 38%,#C9A24D 68%,#E9D6A0 100%)!important;-webkit-background-clip:text!important;background-clip:text!important;-webkit-text-fill-color:transparent!important;color:transparent!important}html body header .header__heading-link .h2::after{display:none!important}@media(max-width:749px){html body header .header__heading-link .h2::before{font-size:1.7rem!important;letter-spacing:.26em!important;margin-right:-.26em!important}}';document.head.appendChild(st);var T=["Box repas : livraison le mardi, Paris et région parisienne","Buffets sur mesure livrés : Mama Blessing, baby shower, anniversaire. Livraison offerte à Paris"],i=0;function tn(m){var w=document.createTreeWalker(m,4),n,l;while(n=w.nextNode())if(n.nodeValue.trim())l=n;return l}function go(){var m=document.querySelector(".announcement-bar__message");if(!m)return;var n=tn(m);if(!n)return;m.style.transition="opacity .6s";m.style.opacity=0;setTimeout(function(){i=(i+1)%2;n.nodeValue=T[i];m.style.opacity=1},650)}setInterval(go,5000)})();;(function(){var P="html:root:root:root:root body:not(.q1):not(.q2) ",D=P+".menu-drawer.menu-drawer ",H=P+"header.header.header.header .header__heading-link.header__heading-link .h2.h2",s=document.createElement("style");s.id="opo-v4";s.textContent=D+"a,"+D+"summary,"+D+".menu-drawer__menu-item,"+D+".list-menu__item,"+D+"span,"+D+"button,"+D+"svg{color:#3B2A22!important}"+D+".menu-drawer__menu-item{background:transparent!important}"+H+"::before{content:'OPONOPONO'!important;font-family:'Cormorant Garamond',Georgia,serif!important;font-weight:500!important;font-size:2.3rem!important;letter-spacing:.34em!important;margin:0 -.34em 0 0!important;background:linear-gradient(100deg,#E9D6A0 0,#F8EBC4 38%,#C9A24D 68%,#E9D6A0 100%)!important;-webkit-background-clip:text!important;background-clip:text!important;-webkit-text-fill-color:transparent!important;color:transparent!important}"+H+"::after{display:none!important}@media(max-width:749px){"+H+"::before{font-size:1.7rem!important;letter-spacing:.26em!important;margin-right:-.26em!important}}";document.head.appendChild(s)})();;(function(){var s=document.createElement('style');s.id='opo-v5';s.textContent='html:root:root:root:root body:not(.q1):not(.q2) .header__heading-link,html:root:root:root:root body:not(.q1):not(.q2) .header__heading-link .h2,html:root:root:root:root body:not(.q1):not(.q2) .header__heading-link .h2::before{white-space:nowrap!important;max-width:none!important;overflow:visible!important}';document.head.appendChild(s)})();;(function(){function f(){var e=document.getElementById('opo-ct');if(e&&!e.dataset.k){e.dataset.k=1;e.innerHTML='<a href="https://www.oponopono-paris.com/pages/contact" style="text-decoration:none;color:inherit;text-transform:uppercase;letter-spacing:.14em">Contact</a>'}}f();var n=0,t=setInterval(function(){f();if(++n>30)clearInterval(t)},300);document.addEventListener('DOMContentLoaded',f)})();;(function(){function g(){var e=document.getElementById('opo-ct');if(e&&!e.dataset.p){var a=e.querySelector('a');if(a){e.dataset.p=1;var b=a.cloneNode(true);b.textContent='Presse';b.style.marginLeft='2.4em';e.appendChild(b)}}}g();var n=0,t=setInterval(function(){g();if(++n>30)clearInterval(t)},300);document.addEventListener('DOMContentLoaded',g)})();;(function(){function h(){var e=document.getElementById('opo-ct');if(e){e.style.textAlign='right';e.style.paddingRight='2rem'}}h();var n=0,t=setInterval(function(){h();if(++n>30)clearInterval(t)},300);document.addEventListener('DOMContentLoaded',h)})();